import React, { useEffect, memo } from 'react';
import type { FieldForInitialize } from '@atlassian/ui-modifications-core/types';
import {
	useFieldIsBusy,
	useTriggerFieldInitialization,
	useAdjustedField,
} from '@atlassian/ui-modifications-core/controllers';
import { useFormApi } from '@atlassian/jira-issue-form-api/src/controllers/index.tsx';
import { getNormalizedType } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/get-normalized-field-type/index.tsx';
import { ConditionallyVisibleDiv } from './styled.tsx';
import type { DecoratedField, EnabledFieldDecoratorProps } from './types.tsx';
import { pickPropertiesToOverwrite } from './utils/index.tsx';

const EnabledFieldDecoratorNoMemo = <ComponentProps extends DecoratedField>({
	Component,
	componentProps,
}: EnabledFieldDecoratorProps<ComponentProps>): React.JSX.Element => {
	const {
		fieldId,
		fieldName,
		description,
		fieldType,
		isRequired = false,
		gqlType,
		allowedValues,
	} = componentProps;
	const { setFieldValue, getFieldValue } = useFormApi();
	const [state, { initializeField, clearAppliedValue }] = useAdjustedField(fieldId);
	const [triggerFieldInitializationKey] = useTriggerFieldInitialization();
	const isBusy = useFieldIsBusy(fieldId);

	const isVisible = state.isVisible ?? true;
	const isReadOnly = state.isReadOnly ?? false;
	const isRequiredFromUIM = state.isRequired ?? false;
	const normalizedType = getNormalizedType(fieldType, gqlType);

	useEffect(() => {
		const field: FieldForInitialize = {
			fieldName,
			fieldId,
			description,
			isVisible,
			fieldType: normalizedType ?? fieldType,
			isReadOnly,
			isRequired,
			allowedValues,
			value: getFieldValue(fieldId),
		};

		initializeField({ field, metadata: { fieldRenderer: gqlType } });

		/**
		 * Because of how the GIC works between subsequent modal openings, UI modifications state is persisted.
		 * That's why want to run initializeField only when triggerFieldInitializationKey change and mounting.
		 */
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [triggerFieldInitializationKey, getFieldValue]);

	useEffect(() => {
		if (state.value !== undefined) {
			setFieldValue(fieldId, state.value);
			// Once we have applied the value, we clear it from the state, so we don't apply it again
			// in the future and overwrite whatever the user has changed between now and then
			clearAppliedValue(fieldId);
		}
	}, [state.value, clearAppliedValue, fieldId, setFieldValue]);

	return (
		<ConditionallyVisibleDiv
			key={fieldId}
			isVisible={isVisible}
			data-testid={`ui-modifications-view-gic.ui.field-decorator.enabled-field-decorator.visibility-wrapper.${fieldId}`}
		>
			<Component
				{...componentProps}
				{...pickPropertiesToOverwrite(state)}
				{...(isBusy ? { isReadOnly: true } : {})}
				areUiModificationsBusy={isBusy}
				isRequiredFromUIM={isRequiredFromUIM}
			/>
		</ConditionallyVisibleDiv>
	);
};

export const EnabledFieldDecorator = memo(EnabledFieldDecoratorNoMemo);
